import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  Container
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Logout,
  Dashboard as DashboardIcon,
  Apps as ProjectsIcon,
  Menu as MenuIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { adminAPI } from "../api/admin";
import {
  Project,
  CreateProjectData,
  currentProjectState
} from "../state/atoms";
import ProjectCard from "../components/ProjectCard";
import ProjectForm from "../components/ProjectForm";

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const currentProject = useRecoilValue(currentProjectState);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "info"
  });
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/login");
  };

  // Fetch projects
  const {
    data: projectsData,
    isLoading,
    error
  } = useQuery({
    queryKey: ["adminProjects"],
    queryFn: adminAPI.getAllProjects
  });

  const projects = projectsData?.projects || [];

  // Update project mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data
    }: {
      id: string;
      data: Partial<CreateProjectData>;
    }) => adminAPI.updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
      setEditDialogOpen(false);
      setSelectedProject(null);
      showSnackbar("Project updated successfully!", "success");
    },
    onError: () => {
      showSnackbar("Failed to update project", "error");
    }
  });

  // Delete project mutation
  const deleteMutation = useMutation({
    mutationFn: adminAPI.deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminProjects"] });
      setDeleteDialogOpen(false);
      setSelectedProject(null);
      showSnackbar("Project deleted successfully!", "success");
    },
    onError: () => {
      showSnackbar("Failed to delete project", "error");
    }
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setEditDialogOpen(true);
  };

  const handleUpdateProject = (data: CreateProjectData) => {
    if (selectedProject) {
      updateMutation.mutate({ id: selectedProject.id, data });
    }
  };

  const handleDeleteProject = (project: Project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedProject) {
      deleteMutation.mutate(selectedProject.id);
    }
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      showSnackbar("Embed code copied to clipboard!", "success");
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      showSnackbar("Failed to copy code", "error");
    }
  };

  const getEmbedCode = (project: Project) => {
    return `<script src="${window.location.origin}/embed/${project.unique_code}/script.js"></script>`;
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Alert severity="error">
          Failed to load projects. Please try again.
        </Alert>
      </Box>
    );
  }

  // Check if current project is super user
  if (!currentProject?.is_super_project) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Alert severity="error">
          Access denied. Super user privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh" }}>
      {/* App Bar */}
      <AppBar
        position="static"
        elevation={0}
      >
        <Toolbar>
          <Box
            component="img"
            src="/assets/images/logoWithWhiteText.png"
            alt="WebPal logo"
            sx={{
              height: { xs: 32, sm: 40 },
              marginRight: "auto",
              cursor: "pointer",
              filter: "drop-shadow(0 2px 8px rgba(255, 237, 0, 0.2))",
              transition: "all 0.3s ease",
              "&:hover": {
                filter: "drop-shadow(0 4px 12px rgba(255, 237, 0, 0.3))",
                transform: "scale(1.02)"
              }
            }}
          />
          {/* Mobile Menu Button */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            sx={{
              margin: 0,
              padding: 0,
              display: { xs: "block", sm: "none" }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              gap: 1,
              alignItems: "center"
            }}
          >
            {/* Super User Navigation */}
            <Button
              startIcon={<DashboardIcon />}
              variant="outlined"
              size="small"
              onClick={() => navigate("/dashboard")}
                sx={{
                fontSize: "0.875rem",
                px: 2
              }}
            >
              Dashboard
            </Button>
            <Button
              startIcon={<ProjectsIcon />}
              variant="outlined"
              size="small"
              sx={{
                borderColor: "#FFED00",
                color: "#FFED00",
                fontWeight: 600,
                fontSize: "0.875rem",
                px: 2,
                "&:hover": {
                  borderColor: "#FFF27A",
                  background: "rgba(255, 237, 0, 0.1)"
                }
              }}
            >
              Projects
            </Button>

            <IconButton onClick={handleLogout} color="inherit" size="small">
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <Box
            sx={{
              display: { xs: "block", sm: "none" },
              py: 2,
              px: 2
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1
              }}
            >
              <Button
                startIcon={<DashboardIcon />}
                variant="outlined"
                size="small"
                onClick={() => {
                  navigate("/dashboard");
                  setMobileMenuOpen(false);
                }}
                sx={{
                  borderColor: "#3D474E",
                  color: "#e8ecf1",
                  fontSize: "0.875rem",
                  px: 2,
                  "&:hover": {
                    borderColor: "#FFED00",
                    background: "rgba(255, 237, 0, 0.1)",
                    color: "#FFED00"
                  }
                }}
              >
                Dashboard
              </Button>
              <Button
                startIcon={<ProjectsIcon />}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#3D474E",
                  color: "#e8ecf1",
                  fontSize: "0.875rem",
                  px: 2,
                  "&:hover": {
                    borderColor: "#FFED00",
                    background: "rgba(255, 237, 0, 0.1)",
                    color: "#FFED00"
                  }
                }}
              >
                Projects
              </Button>

              <Button
                startIcon={<Logout />}
                variant="outlined"
                size="small"
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                sx={{
                  fontSize: "0.875rem",
                  px: 2,
                  borderColor: "#3D474E",
                  color: "#e8ecf1",
                  "&:hover": {
                    borderColor: "#FFED00",
                    background: "rgba(255, 237, 0, 0.1)",
                    color: "#FFED00"
                  }
                }}
              >
                Logout
              </Button>
            </Box>
          </Box>
        )}
      </AppBar>

      <Container
        maxWidth="xl"
        sx={{
          mt: { xs: 2, sm: 4 },
          pb: { xs: 2, sm: 4 },
          px: { xs: 2, sm: 3 }
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: "bold",
              color: "#fff",
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" }
            }}
          >
            Project Management
          </Typography>
        </Box>

        {projects.length === 0 ? (
          <Card elevation={2}>
            <CardContent sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No projects found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Projects are created through Telegram bot integration
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard
                  project={project}
                  onEdit={handleEditProject}
                  onDelete={handleDeleteProject}
                  onCopyCode={handleCopyCode}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Edit Project Dialog */}
        <ProjectForm
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedProject(null);
          }}
          onSubmit={handleUpdateProject}
          project={selectedProject}
          loading={updateMutation.isPending}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.05)"
            }
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              display: "flex",
              alignItems: "center",
              gap: 2
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ffebee, #ffcdd2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <DeleteIcon sx={{ color: "#3D474E", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 0.5
                }}
              >
                Delete Project
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 400
                }}
              >
                This action cannot be undone
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 2, pb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                lineHeight: 1.6
              }}
            >
              Are you sure you want to delete{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: "primary.main",
                  background: "rgba(61, 71, 78, 0.3)",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  display: "inline-block"
                }}
              >
                "{selectedProject?.name}"
              </Box>
              ? This will permanently remove the project and all its data.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              size="large"
              sx={{
                flex: 1,
                borderColor: "rgba(0, 0, 0, 0.12)",
                color: "text.secondary",
                fontWeight: 500,
                py: 1.5,
                "&:hover": {
                  borderColor: "rgba(0, 0, 0, 0.3)",
                  background: "rgba(0, 0, 0, 0.04)"
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              size="large"
              disabled={deleteMutation.isPending}
              startIcon={
                deleteMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteIcon />
                )
              }
              sx={{
                flex: 1,
                background: "linear-gradient(135deg, #3D474E, #2C2F38)",
                color: "#fff",
                fontWeight: 600,
                py: 1.5,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #2C2F38, #3D474E)",
                  boxShadow: "0 6px 16px rgba(0, 0, 0, 0.4)",
                  transform: "translateY(-1px)"
                },
                "&:disabled": {
                  background: "#2C2F38",
                  color: "#3D474E",
                  boxShadow: "none"
                }
              }}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Project"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Embed Code Dialog */}
        {selectedProject && (
          <Dialog
            open={!!copiedCode}
            onClose={() => setCopiedCode(null)}
            PaperProps={{
              sx: {
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.12))",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                borderRadius: 14,
                boxShadow:
                  "0 25px 50px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)"
              }
            }}
          >
            <DialogTitle
              sx={{
                fontFamily: '"Montserrat", "Inter", sans-serif',
                fontWeight: 700,
                background: "linear-gradient(90deg, #14b4ff, #a020f0)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent"
              }}
            >
              Embed Code
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Copy this code and paste it in the &lt;head&gt; section of your
                website:
              </Typography>
              <TextField
                value={getEmbedCode(selectedProject)}
                multiline
                rows={3}
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: {
                    background: "rgba(255, 255, 255, 0.1)",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.25)"
                    }
                  },
                  endAdornment: (
                    <Tooltip title="Copy">
                      <IconButton
                        onClick={() =>
                          handleCopyCode(getEmbedCode(selectedProject))
                        }
                        sx={{
                          color: "#3cc8ff",
                          "&:hover": {
                            background: "rgba(20, 180, 255, 0.1)",
                            transform: "scale(1.1)"
                          }
                        }}
                      >
                        {copiedCode === getEmbedCode(selectedProject) ? (
                          <CheckIcon />
                        ) : (
                          <CopyIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  )
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCopiedCode(null)}
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "#fff",
                  background: "rgba(255, 255, 255, 0.08)",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    background: "rgba(255, 255, 255, 0.15)",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)"
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Projects;
